import {
  Button,
  ControlGroup,
  Group,
  Groups,
  Input,
  IControlChangeEventArgs,
  Select,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import {
  generateLocaleOptions,
  generateUserTypeOptions,
  generateTimezoneOptions,
  LocaleId,
  timezoneIdToValue,
  timezoneValueToId,
} from '@/features/users/user-utils'
import { UserTypeId } from '@/features/users/userApi'
import { generateId } from '@/utils/id-generator'
import { isValidEmail } from '@/utils/validation'

export const FIRST_NAME_MAX = 30
export const LAST_NAME_MAX = 30
export const TITLE_MAX = 70
export const PHONE_MAX = 30

/**
 * The values every Create User form collects. `accountId` is only populated by
 * the standalone (GIM UI) variant, which shows the Account ID field; the embedded
 * (Workflow/DAM) variant resolves its account from the host and omits it.
 */
export interface UserCreateFormValues {
  accountId: string
  firstName: string
  lastName: string
  email: string
  title: string
  phone: string
  locale: LocaleId
  timezone: string
  userTypeId: UserTypeId | null
}

interface FieldError {
  key: string
  params?: Record<string, unknown>
}

type FormErrors = Partial<Record<keyof UserCreateFormValues, FieldError>>

export interface UserCreateFormProps {
  /** Show the Account ID input (standalone GIM UI). The embed resolves the account from the host. */
  showAccountIdField?: boolean
  /** Whether the user may choose the new user's type. When false the field is hidden (KPS-442). */
  canChooseUserType?: boolean
  /** Initial user type — standalone leaves it unset; the embed defaults to Team Member. */
  defaultUserTypeId?: UserTypeId | null
  /** True while the create request is in flight — disables Cancel and shows the submitting label. */
  isSubmitting: boolean
  /** Server-side error to show inline on the email field (e.g. email already in use). */
  emailError?: string
  onSubmit: (values: UserCreateFormValues) => void
  onCancel: () => void
  /** Called when the user edits the email, so the parent can clear a server-side email error. */
  onEmailChange?: () => void
  /** Optional accessible name applied to the <form> element (e.g. for the public invite view). */
  formAriaLabel?: string
}

const buildInitialValues = (
  defaultUserTypeId: UserTypeId | null,
): UserCreateFormValues => ({
  accountId: '',
  firstName: '',
  lastName: '',
  email: '',
  title: '',
  phone: '',
  locale: LocaleId.AccountDefault,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  userTypeId: defaultUserTypeId,
})

/**
 * Single source of truth for the Create User form. Rendered as a standalone view
 * in the GIM UI and, unchanged, inside an embeddable wrapper in the Workflow/DAM
 * UI — the two only differ by the props above, so their fields and validation can
 * never drift apart again (KPS-448). Renders just the fields plus Submit/Cancel;
 * the surrounding page chrome (or lack of it) is the caller's responsibility.
 *
 * Validation is intentionally identical in both contexts and surfaces as inline
 * per-field errors rather than a toast.
 */
export const UserCreateForm = ({
  showAccountIdField = false,
  canChooseUserType = true,
  defaultUserTypeId = null,
  isSubmitting,
  emailError,
  onSubmit,
  onCancel,
  onEmailChange,
  formAriaLabel,
}: UserCreateFormProps): React.ReactElement => {
  const { t } = useTranslation()

  // When the User Type field is hidden (canChooseUserType=false) fall back to
  // Team Member so the form stays submittable even if the caller doesn't pin a
  // default — a hidden required field the user can't fix would otherwise dead-end
  // the form. Mirrors KPS-442 (a non-admin can only create Team Members).
  const initialUserTypeId =
    defaultUserTypeId ?? (canChooseUserType ? null : UserTypeId.TeamMember)

  const [values, setValues] = React.useState<UserCreateFormValues>(() =>
    buildInitialValues(initialUserTypeId),
  )
  const [errors, setErrors] = React.useState<FormErrors>({})

  const userTypeOptions = React.useMemo(() => generateUserTypeOptions(t), [t])
  const localeOptions = React.useMemo(() => generateLocaleOptions(t), [t])
  // Not translated — IANA zone names are identifiers, so the list is stable.
  const timezoneOptions = React.useMemo(() => generateTimezoneOptions(), [])

  const validate = (candidate: UserCreateFormValues): FormErrors => {
    const next: FormErrors = {}

    if (showAccountIdField) {
      // Digits-only + positive: a number input still admits "1.5"/"1e3", and
      // parseInt("1.5") === 1 would silently submit against the wrong account.
      const raw = candidate.accountId.trim()
      if (!/^\d+$/.test(raw) || parseInt(raw, 10) <= 0) {
        next.accountId = { key: 'ACCOUNT_ID_REQUIRED' }
      }
    }

    if (!candidate.firstName.trim()) {
      next.firstName = { key: 'FIRST_NAME_REQUIRED' }
    } else if (candidate.firstName.trim().length > FIRST_NAME_MAX) {
      next.firstName = {
        key: 'FIRST_NAME_MAX_LENGTH',
        params: { max: FIRST_NAME_MAX },
      }
    }

    if (!candidate.lastName.trim()) {
      next.lastName = { key: 'LAST_NAME_REQUIRED' }
    } else if (candidate.lastName.trim().length > LAST_NAME_MAX) {
      next.lastName = {
        key: 'LAST_NAME_MAX_LENGTH',
        params: { max: LAST_NAME_MAX },
      }
    }

    if (!candidate.email.trim()) {
      next.email = { key: 'EMAIL_REQUIRED' }
    } else if (!isValidEmail(candidate.email.trim())) {
      next.email = { key: 'EMAIL_INVALID' }
    }

    if (candidate.title.trim().length > TITLE_MAX) {
      next.title = { key: 'TITLE_MAX_LENGTH', params: { max: TITLE_MAX } }
    }

    if (candidate.phone.trim().length > PHONE_MAX) {
      next.phone = { key: 'PHONE_MAX_LENGTH', params: { max: PHONE_MAX } }
    }

    if (candidate.userTypeId === null) {
      next.userTypeId = { key: 'USER_TYPE_REQUIRED' }
    }

    return next
  }

  const setField = React.useCallback(
    (field: keyof UserCreateFormValues) => (e: IControlChangeEventArgs) => {
      if (field === 'email') onEmailChange?.()
      // A field that was flagged clears its inline error as soon as it's edited.
      setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev))
      setValues(prev => ({ ...prev, [field]: e.currentValue }))
    },
    [onEmailChange],
  )

  const setUserType = (e: IControlChangeEventArgs) => {
    const value = Number(e.currentValue)
    setErrors(prev =>
      prev.userTypeId ? { ...prev, userTypeId: undefined } : prev,
    )
    setValues(prev => ({
      ...prev,
      userTypeId:
        value === UserTypeId.Admin || value === UserTypeId.TeamMember
          ? value
          : null,
    }))
  }

  const setLocale = (e: IControlChangeEventArgs) => {
    const parsed = Number(e.currentValue)
    setValues(prev => ({
      ...prev,
      locale:
        Number.isInteger(parsed) && parsed in LocaleId
          ? (parsed as LocaleId)
          : LocaleId.AccountDefault,
    }))
  }

  const setTimezone = (e: IControlChangeEventArgs) => {
    const zone = timezoneIdToValue(Number(e.currentValue))
    setValues(prev => ({ ...prev, timezone: zone }))
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const nextErrors = validate(values)
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    setErrors({})
    onSubmit(values)
  }

  // Server-side email errors (e.g. conflict) win over client validation for the email field.
  const emailHelpText =
    emailError ?? (errors.email ? t(errors.email.key) : undefined)
  const errorText = (field: keyof UserCreateFormValues): string | undefined => {
    const err = errors[field]
    return err ? t(err.key, err.params) : undefined
  }

  return (
    <form onSubmit={handleSubmit} aria-label={formAriaLabel}>
      <Groups>
        <Group tag='div'>
          {showAccountIdField && (
            <ControlGroup
              label={t('ACCOUNT_ID')}
              helpText={errorText('accountId')}
              control={
                <Input
                  type='number'
                  dataTestingId={generateId('input', 'account-id')}
                  dataTrackingId={generateId('input', 'account-id')}
                  handleChange={setField('accountId')}
                  id={generateId('input', 'account-id')}
                  value={values.accountId}
                  required
                  hasError={!!errors.accountId}
                />
              }
            />
          )}
          <ControlGroup
            label={t('FIRST_NAME')}
            helpText={errorText('firstName')}
            control={
              <Input
                dataTestingId={generateId('input', 'user-firstname')}
                dataTrackingId={generateId('input', 'user-firstname')}
                handleChange={setField('firstName')}
                id={generateId('input', 'user-firstname')}
                value={values.firstName}
                required
                maxLength={FIRST_NAME_MAX}
                hasError={!!errors.firstName}
              />
            }
          />
          <ControlGroup
            label={t('LAST_NAME')}
            helpText={errorText('lastName')}
            control={
              <Input
                dataTestingId={generateId('input', 'user-lastname')}
                dataTrackingId={generateId('input', 'user-lastname')}
                handleChange={setField('lastName')}
                id={generateId('input', 'user-lastname')}
                value={values.lastName}
                required
                maxLength={LAST_NAME_MAX}
                hasError={!!errors.lastName}
              />
            }
          />
          <ControlGroup
            label={t('EMAIL')}
            helpText={emailHelpText}
            control={
              <Input
                type='email'
                dataTestingId={generateId('input', 'user-email')}
                dataTrackingId={generateId('input', 'user-email')}
                handleChange={setField('email')}
                id={generateId('input', 'user-email')}
                value={values.email}
                required
                hasError={!!emailError || !!errors.email}
              />
            }
          />
          <ControlGroup
            label={t('TITLE')}
            helpText={errorText('title')}
            control={
              <Input
                dataTestingId={generateId('input', 'user-title')}
                dataTrackingId={generateId('input', 'user-title')}
                handleChange={setField('title')}
                id={generateId('input', 'user-title')}
                value={values.title}
                maxLength={TITLE_MAX}
                hasError={!!errors.title}
              />
            }
          />
          <ControlGroup
            label={t('PHONE')}
            helpText={errorText('phone')}
            control={
              <Input
                type='tel'
                dataTestingId={generateId('input', 'user-phone')}
                dataTrackingId={generateId('input', 'user-phone')}
                handleChange={setField('phone')}
                id={generateId('input', 'user-phone')}
                value={values.phone}
                maxLength={PHONE_MAX}
                hasError={!!errors.phone}
              />
            }
          />
          <ControlGroup
            label={t('LOCALE')}
            control={
              <Select
                id={generateId('select', 'user-locale')}
                selectedValue={values.locale}
                selectOptions={localeOptions}
                handleChange={setLocale}
                dataTestingId={generateId('select', 'user-locale')}
                dataTrackingId={generateId('select', 'user-locale')}
              />
            }
          />
          <ControlGroup
            label={t('TIMEZONE')}
            control={
              <Select
                dataTestingId={generateId('select', 'user-timezone')}
                dataTrackingId={generateId('select', 'user-timezone')}
                handleChange={setTimezone}
                id={generateId('select', 'user-timezone')}
                selectedValue={timezoneValueToId(values.timezone)}
                selectOptions={timezoneOptions}
              />
            }
          />
          {canChooseUserType && (
            <ControlGroup
              label={t('USER_TYPE')}
              helpText={errorText('userTypeId')}
              control={
                <Select
                  id={generateId('select', 'user-type')}
                  selectedValue={values.userTypeId ?? -1}
                  selectOptions={userTypeOptions}
                  handleChange={setUserType}
                  required
                  dataTestingId={generateId('select', 'user-type')}
                  dataTrackingId={generateId('select', 'user-type')}
                />
              }
            />
          )}
        </Group>
        <Group tag='div'>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              label={t('CANCEL')}
              onClick={onCancel}
              theme='flat'
              disabled={isSubmitting}
            />
            <Button
              label={t(isSubmitting ? 'CREATING_USER' : 'CREATE_USER')}
              type='submit'
              theme='primary'
              loading={isSubmitting}
            />
          </div>
        </Group>
      </Groups>
    </form>
  )
}
